<script setup lang="ts">
interface Props {
  variant?: 'solid' | 'outline' | 'ghost' | 'link'
  color?: 'primary' | 'success' | 'warning' | 'danger' | 'neutral'
  size?: 'sm' | 'md' | 'lg'
  loading?: boolean
  disabled?: boolean
  icon?: string
  iconRight?: string
}

withDefaults(defineProps<Props>(), {
  variant: 'solid',
  color: 'primary',
  size: 'md',
})

const emit = defineEmits<{
  (e: 'click', event: MouseEvent): void
}>()

function handleClick(event: MouseEvent): void {
  emit('click', event)
}
</script>

<template>
  <button
    class="aw-button"
    :class="[
      `aw-button--${variant}`,
      `aw-button--${color}`,
      `aw-button--${size}`,
      { 'aw-button--loading': loading, 'aw-button--disabled': disabled },
    ]"
    :disabled="disabled || loading"
    @click="handleClick"
  >
    <span v-if="loading" class="aw-button__spinner" />
    <span v-if="icon && !loading" class="aw-button__icon">{{ icon }}</span>
    <span class="aw-button__content">
      <slot />
    </span>
    <span v-if="iconRight" class="aw-button__icon aw-button__icon--right">{{ iconRight }}</span>
  </button>
</template>

<style scoped>
.aw-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--aw-space-1);
  border: 1px solid transparent;
  border-radius: var(--aw-radius-md);
  font-family: var(--aw-font-sans);
  font-weight: var(--aw-font-medium);
  cursor: pointer;
  transition: all var(--aw-duration-fast) var(--aw-ease-linear);
  white-space: nowrap;
  position: relative;
}

/* Sizes */
.aw-button--sm {
  height: 28px;
  padding: 0 var(--aw-space-3);
  font-size: var(--aw-text-sm);
  border-radius: var(--aw-radius-sm);
}
.aw-button--md {
  height: 32px;
  padding: 0 var(--aw-space-4);
  font-size: var(--aw-text-base);
}
.aw-button--lg {
  height: 40px;
  padding: 0 var(--aw-space-5);
  font-size: var(--aw-text-base);
}

/* Variants: solid */
.aw-button--solid.aw-button--primary {
  background-color: var(--aw-primary-500);
  color: var(--aw-text-inverse);
}
.aw-button--solid.aw-button--primary:hover {
  background-color: var(--aw-primary-600);
}
.aw-button--solid.aw-button--danger {
  background-color: var(--aw-danger-500);
  color: var(--aw-text-inverse);
}
.aw-button--solid.aw-button--danger:hover {
  background-color: var(--aw-danger-600);
}

/* Variants: outline */
.aw-button--outline {
  background-color: transparent;
  border-color: var(--aw-border-default);
  color: var(--aw-text-primary);
}
.aw-button--outline:hover {
  border-color: var(--aw-primary-300);
  color: var(--aw-primary-600);
}

/* Variants: ghost */
.aw-button--ghost {
  background-color: transparent;
  color: var(--aw-text-primary);
}
.aw-button--ghost:hover {
  background-color: var(--aw-bg-muted);
}

/* States */
.aw-button:active:not(:disabled) {
  transform: scale(0.98);
}
.aw-button--disabled,
.aw-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

/* Loading */
.aw-button__spinner {
  width: 14px;
  height: 14px;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: var(--aw-radius-full);
  animation: aw-spin 0.75s linear infinite;
}
@keyframes aw-spin {
  to { transform: rotate(360deg); }
}
</style>